import { useState, useEffect, useRef } from 'react'
import { useAuthStore } from '../../store/authStore'
import api from '../../services/api'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import { User, Mail, Phone, MapPin, Calendar, Save, Camera, Clock, Package, ShieldCheck, CheckCircle, ArrowRight } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'

export default function Profile() {
  const { user, updateUser } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [studentData, setStudentData] = useState(null)
  const fileInputRef = useRef(null)
  const [studentEquipment, setStudentEquipment] = useState(null)
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    preferred_study_time: ''
  })

  useEffect(() => {
    fetchProfile()
  }, [])

  const fetchProfile = async () => {
    try {
      const { data } = await api.get('/students/profile')
      setStudentData(data)
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        address: data.address || '',
        date_of_birth: data.date_of_birth ? data.date_of_birth.split('T')[0] : '',
        preferred_study_time: data.preferred_study_time || ''
      })

      if (data?.id) {
        try {
          const eqRes = await api.get(`/equipment/student/${data.id}`)
          setStudentEquipment(eqRes.data)
        } catch (eqErr) {
          console.error('Failed to load student equipment:', eqErr)
        }
      }
    } catch (error) {
      toast.error('Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      await api.put('/students/profile', formData)
      toast.success('Profile updated successfully')
      updateUser({ first_name: formData.first_name, last_name: formData.last_name })
      fetchProfile()
    } catch (error) {
      toast.error('Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB')
      return
    }
    setUploadingPhoto(true)
    try {
      const reader = new FileReader()
      reader.onloadend = async () => {
        try {
          const base64 = reader.result
          await api.put('/students/profile/avatar', { profile_image: base64 })
          setStudentData(prev => ({ ...prev, profile_image: base64 }))
          toast.success('Profile picture updated!')
        } catch {
          toast.error('Failed to upload photo')
        } finally {
          setUploadingPhoto(false)
        }
      }
      reader.readAsDataURL(file)
    } catch {
      toast.error('Failed to read image')
      setUploadingPhoto(false)
    }
  }

  const inputCls = "w-full bg-white/5 border border-white/10 text-white text-sm font-sans px-4 py-2.5 focus:outline-none focus:border-cinema-glow/40 transition-colors placeholder:text-cinema-muted/40"

  if (loading) return <LoadingSpinner label="Loading profile" />

  return (
    <div className="space-y-10">
      <div className="cinema-page-header">
        <div>
          <span className="cinema-label block mb-1">Student Portal</span>
          <h1 className="font-display text-2xl text-white font-light">My Profile</h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-10">
        {/* Profile sidebar */}
        <div className="flex flex-col items-center text-center">
          <div className="relative mb-5">
            {studentData?.profile_image ? (
              <img
                src={studentData.profile_image}
                alt={`${formData.first_name} ${formData.last_name}`}
                className="w-28 h-28 rounded-full object-cover border-2 border-cinema-glow/30"
                loading="lazy" decoding="async"
              />
            ) : (
              <div className="w-28 h-28 rounded-full bg-cinema-glow/15 border-2 border-cinema-glow/30 flex items-center justify-center font-display text-3xl text-cinema-glow font-light">
                {formData.first_name?.[0]}{formData.last_name?.[0]}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="absolute bottom-0 right-0 w-8 h-8 cinema-glass border border-cinema-glow/30 rounded-full flex items-center justify-center text-cinema-glow hover:bg-cinema-glow/10 disabled:opacity-50 transition-all"
              title="Change profile picture"
            >
              {uploadingPhoto
                ? <LoadingSpinner size="sm" inline />
                : <Camera size={14} />}
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
          </div>

          <h2 className="font-display text-lg text-white font-normal mb-0.5">{formData.first_name} {formData.last_name}</h2>
          <p className="font-sans text-sm text-cinema-muted mb-5">{formData.email}</p>

          {studentData && (
            <div className="w-full space-y-3 border-t border-white/5 pt-4 text-left">
              <div className="flex items-start gap-2">
                <Calendar size={14} className="text-cinema-muted mt-0.5 shrink-0" />
                <div>
                  <p className="cinema-label">Enrolled Since</p>
                  <p className="font-sans text-sm text-white mt-0.5 font-medium">{new Date(studentData.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User size={14} className="text-cinema-muted mt-0.5 shrink-0" />
                <div>
                  <p className="cinema-label">Student ID</p>
                  <p className="font-mono text-xs sm:text-sm text-cinema-glow mt-0.5 font-semibold">STU-{studentData.id.toString().padStart(4, '0')}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Profile form */}
        <div className="lg:col-span-2">
          <div className="cinema-section-header">
            <h3 className="cinema-section-title">Personal Information</h3>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="cinema-label block mb-2">First Name</label>
                <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} className={inputCls} required />
              </div>
              <div>
                <label className="cinema-label block mb-2">Last Name</label>
                <input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} className={inputCls} required />
              </div>
            </div>

            <div>
              <label className="cinema-label flex items-center gap-1.5 mb-2"><Mail size={13} /> Email</label>
              <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={inputCls} required />
            </div>

            <div>
              <label className="cinema-label flex items-center gap-1.5 mb-2"><Phone size={13} /> Phone</label>
              <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className={inputCls} />
            </div>

            <div>
              <label className="cinema-label flex items-center gap-1.5 mb-2"><MapPin size={13} /> Address</label>
              <textarea value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className={`${inputCls} resize-none`} rows={3} />
            </div>

            <div>
              <label className="cinema-label flex items-center gap-1.5 mb-2"><Calendar size={13} /> Date of Birth</label>
              <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} className={inputCls} />
            </div>

            <div>
              <label className="cinema-label flex items-center gap-1.5 mb-3"><Clock size={13} /> Preferred Study Time</label>
              <div className="grid grid-cols-3 gap-3">
                {[{ value: '9am', label: '9:00 AM', sub: 'Morning' }, { value: '12pm', label: '12:00 PM', sub: 'Afternoon' }, { value: '6pm', label: '6:00 PM', sub: 'Evening' }].map((slot) => (
                  <label key={slot.value} className={`flex flex-col items-center gap-1 py-3 cursor-pointer transition-all border ${
                    formData.preferred_study_time === slot.value
                      ? 'border-cinema-glow/40 bg-cinema-glow/10 text-cinema-glow'
                      : 'border-white/8 text-cinema-muted hover:border-cinema-glow/20'
                  }`}>
                    <input type="radio" name="profile_study_time" value={slot.value} checked={formData.preferred_study_time === slot.value} onChange={(e) => setFormData({ ...formData, preferred_study_time: e.target.value })} className="sr-only" />
                    <span className="font-sans text-sm font-semibold">{slot.label}</span>
                    <span className="font-sans text-xs opacity-75">{slot.sub}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="submit" disabled={saving} className="cinema-btn-primary text-xs sm:text-sm px-6 py-2.5 min-h-0 flex items-center gap-2 disabled:opacity-50 font-medium">
                <Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={fetchProfile} className="cinema-btn-ghost text-xs sm:text-sm px-5 py-2.5 min-h-0 font-medium">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Equipment & Assets Section */}
      <div className="border-t border-white/10 pt-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h2 className="font-display text-xl text-white font-light flex items-center gap-2">
              <Package className="w-5 h-5 text-cinema-glow" />
              Equipment & Studio Gear
            </h2>
            <p className="text-xs text-cinema-muted mt-0.5">
              Academy production equipment checked out to you or requested for coursework.
            </p>
          </div>
          <Link
            to="/student/equipment"
            className="cinema-btn-primary text-xs px-4 py-2 flex items-center gap-1.5 self-start sm:self-auto"
          >
            Browse & Request Gear <ArrowRight size={13} />
          </Link>
        </div>

        {/* Currently Checked Out — Cardless */}
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-cinema-muted">
            Currently In Your Custody
          </h3>

          {!studentEquipment?.currently_checked_out?.length ? (
            <div className="text-center py-8 text-cinema-muted text-xs">
              <ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-30" />
              No equipment currently checked out.
            </div>
          ) : (
            <div className="divide-y divide-white/10 border-t border-b border-white/10">
              {studentEquipment.currently_checked_out.map((item) => (
                <div key={item.id} className="py-3 flex flex-col sm:flex-row justify-between sm:items-center gap-2 hover:bg-white/[0.01] transition-colors">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-white text-sm">{item.equipment_name}</span>
                      <span className="text-xs text-cinema-glow font-mono">({item.outstanding_quantity} unit{item.outstanding_quantity !== 1 ? 's' : ''})</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${
                        item.is_overdue ? 'bg-red-500/20 text-red-300 font-bold' : 'bg-emerald-500/10 text-emerald-400'
                      }`}>
                        {item.is_overdue ? 'Overdue' : 'Checked Out'}
                      </span>
                    </div>
                    <div className="text-xs text-cinema-muted mt-1 flex flex-wrap gap-3">
                      <span>Issued: {new Date(item.issued_at).toLocaleDateString()}</span>
                      <span className={item.is_overdue ? 'text-red-400 font-semibold' : ''}>
                        Due: {new Date(item.expected_return_date).toLocaleDateString()}
                      </span>
                      <span>Condition: {item.condition_at_issue}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
